import React, { useState } from 'react';
import { api } from '../context/AuthContext';
import { GradingForm, SUBJECTS } from '../components/GradingForm';
import { FeedbackResult, FeedbackPlaceholder } from '../components/FeedbackDisplay';
import { FeedbackSkeleton } from '../components/FeedbackSkeleton';

/**
 * Teacher-facing Mark page — same engine as the student dashboard's Mark tab,
 * giving teachers the ability to grade ad-hoc past paper questions against
 * the same RAG-backed mark schemes + textbooks the platform uses for students.
 */
export default function TeacherMarkPage() {
    const [subject, setSubject] = useState('business');
    const [level, setLevel] = useState('IAL');
    const [question, setQuestion] = useState('');
    const [studentAnswer, setStudentAnswer] = useState('');
    const [answerImages, setAnswerImages] = useState([]);
    const [questionImage, setQuestionImage] = useState(null);
    const [subjectDropdownOpen, setSubjectDropdownOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [error, setError] = useState('');

    const clearForm = () => {
        setQuestion('');
        setStudentAnswer('');
        setAnswerImages([]);
        setQuestionImage(null);
        setFeedback(null);
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setFeedback(null);
        setLoading(true);
        try {
            const requestData = {
                subject: SUBJECTS.find(s => s.id === subject)?.name || subject,
                level,
                question,
                student_answer: studentAnswer,
                mark_scheme: '',
            };
            if (answerImages.length > 0) requestData.answer_image_base64 = answerImages;
            if (questionImage) requestData.question_image_base64 = questionImage;
            const r = await api.post('/api/feedback', requestData);
            setFeedback(r.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to get feedback. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="teacher-mark-page">
            <div>
                <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Mark a question</h1>
                <p className="text-sm text-zinc-500 mt-1">
                    Grade any past paper question against the official mark schemes & textbooks. Use this to test answers before assigning, or to demo the AI to your students.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                    <GradingForm
                        subject={subject}
                        setSubject={setSubject}
                        level={level}
                        setLevel={setLevel}
                        question={question}
                        setQuestion={setQuestion}
                        studentAnswer={studentAnswer}
                        setStudentAnswer={setStudentAnswer}
                        answerImages={answerImages}
                        setAnswerImages={setAnswerImages}
                        questionImage={questionImage}
                        setQuestionImage={setQuestionImage}
                        loading={loading}
                        error={error}
                        onSubmit={handleSubmit}
                        onClear={clearForm}
                        subjectDropdownOpen={subjectDropdownOpen}
                        setSubjectDropdownOpen={setSubjectDropdownOpen}
                    />
                </div>
                <div>
                    {loading ? (
                        <FeedbackSkeleton />
                    ) : feedback ? (
                        <FeedbackResult feedback={feedback} />
                    ) : (
                        <FeedbackPlaceholder />
                    )}
                </div>
            </div>
        </div>
    );
}
